import { useState, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';

interface TrendingSearchResult {
    id: string;
    title: string;
    link: string;
    type: 'Blog' | 'Product' | 'Professional' | 'Project';
}

interface FeaturedResultsResponse {
    results: Array<{
        title: string;
        link: string;
        type: 'Blog' | 'Product' | 'Professional' | 'Project';
        [key: string]: unknown;
    }>;
    totalCount: number;
}

interface CachedTrendingResults {
    results: TrendingSearchResult[];
    timestamp: number;
}

const CACHE_KEY = 'trending_search_results';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Hook to fetch trending search results for the mobile search modal.
 * Fetches the same featured results that desktop "Recommended" tab shows.
 * Returns the top 5 trending search results with title, link, and type information.
 * Results are cached in session storage for 30 minutes to improve performance.
 */
export const useTrendingSearchResults = (): {
    trendingSearchResults: TrendingSearchResult[];
    isLoading: boolean;
} => {
    const [trendingSearchResults, setTrendingSearchResults] = useState<
        TrendingSearchResult[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetcher = useFetcher<FeaturedResultsResponse>();
    const fetcherRef = useRef(fetcher);

    // Update the ref whenever fetcher changes
    useEffect(() => {
        fetcherRef.current = fetcher;
    });

    // Function to check if cached results are still valid
    const getCachedResults = (): TrendingSearchResult[] | null => {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const cachedData: CachedTrendingResults = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is still valid (within 30 minutes)
            if (now - cachedData.timestamp < CACHE_DURATION) {
                return cachedData.results;
            }

            // Cache expired, remove it
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        } catch (error) {
            // If there's any error parsing cached data, remove it
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        }
    };

    // Function to cache results in session storage
    const cacheResults = (results: TrendingSearchResult[]) => {
        try {
            const cacheData: CachedTrendingResults = {
                results,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            // Silently fail if session storage is not available or full
            console.warn('Failed to cache trending search results:', error);
        }
    };

    useEffect(() => {
        // First, try to load from cache
        const cachedResults = getCachedResults();

        if (cachedResults && cachedResults.length > 0) {
            // Use cached results immediately
            setTrendingSearchResults(cachedResults);
            setIsLoading(false);
            return;
        }

        // No valid cache found, fetch fresh data
        const params = new URLSearchParams({
            limit: '5',
        });
        fetcherRef.current.load(
            `/actions/search/featured-results?${params.toString()}`
        );
    }, [fetcherRef]);

    useEffect(() => {
        if (fetcherRef.current.state === 'loading') {
            // Only show loading if we don't have cached results
            const cachedResults = getCachedResults();
            if (!cachedResults || cachedResults.length === 0) {
                setIsLoading(true);
            }
        } else {
            setIsLoading(false);

            if (fetcherRef.current.data?.results) {
                // Convert featured results to trending search results
                const results: TrendingSearchResult[] =
                    fetcherRef.current.data.results
                        .slice(0, 5)
                        .map((result, index: number) => ({
                            id: `trending-${index}`,
                            title: result.title,
                            link: result.link,
                            type: result.type,
                        }));

                setTrendingSearchResults(results);

                // Cache the new results
                cacheResults(results);
            }
        }
    }, [fetcherRef.current.data, fetcherRef.current.state]);

    return {
        trendingSearchResults,
        isLoading,
    };
};
